import React, { Fragment } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import TaxForm from '../forms/TaxForm'
import Button from '../../common/Button'
import LoadingButton from '../../common/LoadingButton'
import FileLink from '../../common/FileLink'
import { graphql, compose, withApollo } from 'react-apollo'
import WithLoan from '../../shared/WithLoan'
import withHelloSign from '../contexts/withHelloSign'
import formNames from '../helpers/formNames'
import TaxReturnOrderInfo from '../queries/TaxReturnOrderInfo'
import DocumentHeaderImage from '../queries/DocumentHeaderImage'
import OrderTaxTranscripts from '../queries/OrderTaxTranscripts'
import findTaxReturnBucket from '../helpers/findTaxReturnBucket'
import { openEsignModal } from '../helpers/EsignUtil'
import UpsertDocumentBucket from '../queries/UpsertDocumentBucket'
import TaxReturnPersonalInfo from '../queries/TaxReturnPersonalInfo'

const Container = styled.section`
  background: white;
  position: relative;
  padding: 20px;
  box-shadow: 0px 2px 7px 0px rgba(0,0,0,0.6);
  width: 800px;
  max-height: 90%;
  overflow: scroll;
`
const Close = styled.span`
  font-weight: bold;
  position: absolute;
  top: 15px;
  right: 15px;
  font-size: 20px;
  cursor: pointer;
`
const Header = styled.div`
  display: flex;
  flex-direction: column;
`
const Name = styled.h2`
  text-align: left;
`
const GreyText = styled.small`
  color: grey;
`
const Select = styled.select`
  width: auto; 
`
const Footer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`
const SubmitButton = styled(LoadingButton)`
  margin-left: 20px;
`
const CloseButton = Button.extend`
  background: #dbdbdc;
  color: #66666a;
`
const TaxFormContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 207px;
  margin-top: 20px;
`
const HeaderImageContainer = styled.div`
  height: 257px;
  width: 800px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
`

export class OrderTranscriptsModal extends React.Component {
  static propTypes = {
    close: PropTypes.func.isRequired,
    document: PropTypes.shape({
      esigned: PropTypes.bool
    }),
    entity: PropTypes.shape({
      name: PropTypes.string,
      taxReturnUrls: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
      }))
    }),
    loan: PropTypes.shape({
      lastTaxReturnOnOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
      user: PropTypes.shape({
        hellosign_api_key: PropTypes.string
      })
    }),
    resourceType: PropTypes.string.isRequired,
    taxReturnOrderInfo: PropTypes.func.isRequired,
    taxReturns: PropTypes.arrayOf(PropTypes.shape({
      year: PropTypes.string,
      formName: PropTypes.string
    })),
    taxReturnUrls: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })),
    upsertDocumentBucket: PropTypes.func
  }
  constructor(props) {
    super()
    const { resourceType } = props
    const { lastTaxReturnOnOptions } = props.loan
    const { taxReturns, documents, fiscalYears } = props.entity
    // const [yearSelected] = last_tax_return_on_options
    const defaultForms = {
      Guarantor: '1040C',
      Business: '1120S'
    }
    const taxReturn = taxReturns.find(tr => parseInt(tr.year, 10) === lastTaxReturnOnOptions[0])
    const formSelected = taxReturn ? taxReturn.formName : defaultForms[resourceType]
    // Find the most recent tax return bucket with a file uploaded and default to that year
    const taxReturnBuckets = lastTaxReturnOnOptions.map(year => ({ document: findTaxReturnBucket(year, fiscalYears, taxReturns, documents, resourceType), year }))
    const mostRecentBucket = taxReturnBuckets.find(b => b.document ? b.document.files.length > 0 : false)
    const yearSelected = mostRecentBucket ? mostRecentBucket.year : lastTaxReturnOnOptions[0]
    this.state = {
      formSelected,
      yearSelected,
      headerImageUrl: mostRecentBucket ? mostRecentBucket.document.header_image_url : null,
      extractionSuccess: mostRecentBucket ? mostRecentBucket.document.files.some(f => f.details === 'SUCCESSFUL') : false,
      formData: {},
      isLoading: false
    }
  }
  onYearChange = e => {
    const yearSelected = parseInt(e.target.value, 10)
    const { documents, taxReturns, fiscalYears } = this.props.entity
    const { resourceType } = this.props
    const taxReturnBucket = findTaxReturnBucket(yearSelected, fiscalYears, taxReturns, documents, resourceType)
    this.setState({
      yearSelected: parseInt(e.target.value, 10),
      headerImageUrl: taxReturnBucket ? taxReturnBucket.header_image_url : null,
      extractionSuccess: taxReturnBucket ? taxReturnBucket.files.some(f => f.details === 'SUCCESSFUL') : false
    })
  }
  onFormChange = e => {
    this.setState({ formSelected: e.target.value, formData: {} })
  }
  onFormDataChange = formData => {
    this.setState({
      formData
    })
  }
  onSubmit = async () => {
    const { hs, entity, resourceType } = this.props
    this.setState({ isLoading: true })
    const documentId = await this.updateTaxReturns(entity.rid, resourceType)
    const helloSignUrl = await this.getHelloSignUrl(documentId)
    openEsignModal(hs, 'sign', this.messageListener, helloSignUrl)
    this.props.close()
  }
  messageListener = async () => {
    const { upsertDocumentBucket, documentId, updateDocumentFiles } = this.props
    await upsertDocumentBucket({
      variables: {
        input: {
          documentId,
          documentFields: {
            esignStatus: 'signed'
          }
        }
      }})
      // Poll the server until HelloSign returns the signed document
      updateDocumentFiles(documentId)
  }
  updateTaxReturns = async (rid, resourceType) => {
    const { taxReturnOrderInfo, taxReturnPersonalInfo } = this.props
    const { taxReturns } = this.props.entity
    const { yearSelected, formData, formSelected } = this.state
    const {
      endDate,
      street,
      tin,
      name,
      state,
      apt,
      city,
      zip
    } = formData
    const taxReturn = taxReturns.find(tr => parseInt(tr.year, 10) === yearSelected)
    if (resourceType === 'Business') {
      const response = await taxReturnOrderInfo({
        variables: {
          input: {
            businessId: rid,
            fiscalYearEndOn: `${endDate ? endDate : '12/31'}/${yearSelected}`,
            lastTaxReturnOn: `${endDate ? endDate : '12/31'}/${yearSelected}`,
            taxReturnFields: {
              formName: formSelected,
              name,
              tin
            },
            addressFields: {
              apt,
              city,
              state,
              zip,
              street,
              addressableId: rid,
              addressableType: 'Business'
            }
          }
        }
      })
      return response.data.taxReturnOrderInfo.orderId
    } else if (resourceType === 'Guarantor') {
      const response = await taxReturnPersonalInfo({
        variables: {
          input: {
            guarantorId: rid,
            addressFields: {
              addressableId: rid,
              addressableType: 'Guarantor',
              apt,
              city,
              state,
              zip,
              street
            },
            taxReturnFields: {
              formId: taxReturn ? taxReturn.formId : 1, // default to 1040C for guarantors
              name,
              tin,
              year: yearSelected
            }
          }
        }
      })
      return response.data.taxReturnPersonalInfo.orderId
    }
  }
  getHelloSignUrl = async documentId => {
    const { loan, orderTaxTranscripts } = this.props
    const { esigned } = this.props.document
    const response = await orderTaxTranscripts({
      variables: {
        documentId,
        loanId: loan.rid,
        resign: esigned
      }
    })
    return response.data.orderTaxTranscripts.signUrl
  }
  renderHeader = () => {
    const { resourceType, entity } = this.props
    const { name } = entity
    return (
      <Header>
        <Name>
          {`${name}'s ${resourceType === 'Business' ? 'Business' : 'Personal'} Tax Transcripts`}
        </Name>
        <GreyText>
          Please verify that the information is correct and then electronically sign the order form.
        </GreyText>
      </Header>
    )
  }
  renderTaxReturn = () => {
    const { taxReturnUrls } = this.props.entity
    const lastTaxYear = Math.max(...taxReturnUrls.map(tr => parseInt(tr.label, 10)))
    const taxReturnUrl = taxReturnUrls.find(tr => parseInt(tr.label, 10) === lastTaxYear)
    if (taxReturnUrl) {
      return (
        <span>
          <p>
            Here is the most recent tax return:
            <FileLink id="last_tax_return" href={taxReturnUrl.value} text={`${lastTaxYear} Tax Return`} />
          </p>
        </span>
      )
    }
  }
  renderTaxYearSelect = () => {
    const { loan, entity } = this.props
    const { name } = entity
    const { lastTaxReturnOnOptions } = loan
    const { yearSelected } = this.state
    return (
      <div>
        <p>What is the most recently filed year for {name}?</p>
        <Select id="tax_year_select" value={yearSelected} onChange={this.onYearChange}>
          {lastTaxReturnOnOptions.map(o => <option value={o} key={`ltro_${o}`}>{o}</option>)}
        </Select>
      </div>
    )
  }
  renderFormSelect = () => {
    const { resourceType } = this.props
    const { formSelected } = this.state
    const formOptions = Object.keys(formNames).map(form => {
      if (resourceType === 'Guarantor' && form !== '1040C') {
        return
      }
      return <option value={form} key={form}>{formNames[form]}</option>
    })
    return (
      <Fragment>
        <p>
          Which form was it filed on?
        </p>
        <Select id="tax_form_select" onChange={this.onFormChange} value={formSelected}>
          {formOptions}
        </Select>
      </Fragment>
    )
  }
  renderHeaderImage = () => {
    const { yearSelected, headerImageUrl } = this.state
    if (headerImageUrl) {
      return (
        <section>
          <b>
            Please confirm that the top of the {yearSelected} tax return matches the information in
            the blue fields below. This includes:
          </b>
          <ul>
            <li>Periods</li>
            <li>Commas</li>
            <li>Capitalization</li>
          </ul>
          <HeaderImageContainer>
            <img src={headerImageUrl} />
          </HeaderImageContainer>
        </section>
      )
    }
  }
  renderFormInput = () => {
    const { taxAddress, taxReturns, financial } = this.props.entity
    const { yearSelected, formSelected, headerImageUrl, extractionSuccess } = this.state
    const taxReturn = taxReturns.find(tr => parseInt(tr.year, 10) === yearSelected)
    const endDate = financial && financial.fiscal_year_end_on ? financial.fiscal_year_end_on.substr(0, 5) : '12/31'
    let defaults = {
      endDate
    }
    if (taxAddress && extractionSuccess) {
      const { city, state, street, zip, apt } = taxAddress
      defaults = {
        city,
        state,
        street,
        zip,
        apt
      }
    }
    if (taxReturn && extractionSuccess) {
      const { firstName, lastName, tin, name } = taxReturn
      if (formSelected === '1040C') {
        defaults = {
          ...defaults,
          firstName,
          lastName,
          tin
        }
      } else {
        defaults = {
          ...defaults,
          name,
          tin
        }
      }
    }
    const handler = {
      get: (obj, prop) => {
        return obj[prop] ? obj[prop] : ''
      }
    }
    // TODO: This proxy may be unnecessary, Cleave was complaining about default values being null or undefined
    // warrants further investigation
    const proxiedDefaults = new Proxy(defaults, handler) // Any nonexistent properties will return an empty string ('')
    // We only use defaults if DocGenie was able to successfully extract tax return data, evidenced by a header image url
    return (
      <section>
        <TaxFormContainer>
          <TaxForm
            form={formSelected}
            year={yearSelected}
            onDataChange={this.onFormDataChange}
            defaults={proxiedDefaults}
          />
        </TaxFormContainer>
        <p>
          <i>
            Please check that the information above matches the {yearSelected} tax return.
          </i>
        </p>
      </section>
    )
  }
  renderFooter = () => {
    const { isLoading } = this.state
    const { isValid } = this.state.formData
    const { close } = this.props
    return (
      <Footer>
        <CloseButton onClick={close} id="cancel_order_transcripts">
          Cancel
        </CloseButton>
        <SubmitButton
          id="submit_transcript_order"
          onClick={this.onSubmit}
          disabled={!isValid}
          loading={isLoading}
        >
          Electronically Sign Order
        </SubmitButton>
      </Footer>
    )
  }
  render() {
    const { close } = this.props
    return (
      <Container id="order_transcripts_modal">
        <Close id="close_order_transcripts_modal" onClick={close}>
          X
        </Close>
        {this.renderHeader()}
        {this.renderTaxReturn()}
        {this.renderTaxYearSelect()}
        {this.renderFormSelect()}
        {this.renderHeaderImage()}
        {this.renderFormInput()}
        {this.renderFooter()}
      </Container>
    )
  }
}

export default compose(
  graphql(TaxReturnOrderInfo, {
    name: 'taxReturnOrderInfo'
  }),
  graphql(DocumentHeaderImage, {
    name: 'documentHeaderImage'
  }),
  graphql(OrderTaxTranscripts, {
    name: 'orderTaxTranscripts'
  }),
  graphql(UpsertDocumentBucket, {
    name: 'upsertDocumentBucket'
  }),
  graphql(TaxReturnPersonalInfo, {
    name: 'taxReturnPersonalInfo'
  }),
  WithLoan,
  withApollo,
  withHelloSign
)(OrderTranscriptsModal)
